import React, { useEffect, ReactNode } from 'react'
import { Search, Bookmark, HelpCircle, Settings, Building, PencilLineIcon as PencilLine, CreditCardIcon as CreditCard, ExternalLink } from '../../utils/icons'
import { useViewTransitionNavigate } from '../ui/ViewTransitionLink'

import SideNav from './SideNav.tsx'
import { MissionControl, Navigation, NavBrand, NavIconButton, NavActions, AppSwitcherButton, AccountMenu, SidebarProvider, SidebarInset, useSidebar, type NavActionItem, type AccountMenuItem, type AccountMenuWorkspace } from 'prosci-ui'
import { hasWorkspacesAccess, useWorkspaces, toAccountMenuWorkspaces, type WorkspaceOption } from '../../features/dashboard/components/useWorkspaces'
import { useProductSwitcherApps } from '../../hooks/useProductSwitcherApps'
import { useProductDrawerQuery } from '../../hooks/queries/useProductDrawerQuery'
import type { DrawerProduct } from '../../utils/productDisplayUtils'

// Helper to get initials from name
function getInitials(name: string) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

import {
  APP_IMAGE_MAPPING,
  APP_MAPPING_BY_SLUG,
  APP_PATH_MAPPING,
  CONTACT_LINK,
  AI_HELP_LINK,
  PRIVACY_POLICY_LINK,
  TERMS_CONDITIONS_LINK,
} from '../../config';
import { aiLogoFull as kaiyaLogo, aiLogo as kaiyaIcon } from '../../assets/images';
import {
  useAppCopy,
  useHubAppCopy,
  useExpandedNavItems,
  useExpandedSideNav,
} from '../../hooks';
import { useApp } from '../../contexts/AppContext'
import { useAuth } from '../../contexts/AuthContext'

interface NavigationLayoutProps {
  children: ReactNode
  program?: Record<string, unknown>
  programs: Record<string, unknown>[]
  userApps: string[]
  activeHubSlug?: string
  product?: string
  masqueradedUser?: Record<string, unknown>
}

function topNavActionItems(
  hubAppCopy: (key: string) => string,
  navigate: (path: string) => void
): NavActionItem[] {
  // Icon wrappers vs. NavActionItem's icon type disagree across the prosci-ui/app @types/react boundary.
  return [
    { key: 'search', label: hubAppCopy('search'), icon: Search as any, onSelect: () => navigate('/search'), shared: true }, // eslint-disable-line @typescript-eslint/no-explicit-any
    { key: 'bookmarks', label: hubAppCopy('bookmark'), icon: Bookmark as any, onSelect: () => navigate('/bookmarks'), shared: true }, // eslint-disable-line @typescript-eslint/no-explicit-any
  ]
}

function missionControlNavItems(
  userApps: string[],
  appCopy: (key: string) => string,
  hubAppCopy: (key: string) => string
) {
  return userApps.map((userApp) => ({
    path: APP_PATH_MAPPING[userApp as keyof typeof APP_PATH_MAPPING],
    logo: APP_IMAGE_MAPPING[userApp as keyof typeof APP_IMAGE_MAPPING],
    logoAlt: userApp === 'changeCoach'
      ? hubAppCopy('summaryCopy.productName')
      : appCopy(userApp),
  }))
}

function getPortalRoleLabel(currentUser: Record<string, unknown>, portalCopy: { customerTypes?: { direct?: string; licenseAdmin?: string; licenseUser?: string } }) {
  if (currentUser.customerTypeId == 1) return portalCopy.customerTypes?.direct || 'Prosci Customer'
  if (currentUser.customerTypeId === 2) return portalCopy.customerTypes?.licenseAdmin || 'License Administrator'
  return portalCopy.customerTypes?.licenseUser || 'Licensed Customer'
}

// Redirects into the admin portal via a short-lived access token minted for the current user.
async function goToManageOrganization() {
  const { api } = await import('../../utils/apiClient');
  try {
    const response = await api.createAdminAccessToken();
    const token = response?.data?.attributes?.accessToken;
    const adminUrl = response?.data?.attributes?.adminUrl;
    if (token && adminUrl) {
      window.location.href = `${adminUrl}/customers/login?access=${token}`;
    } else {
      console.error('Failed to get token or admin URL');
      window.location.href = '/?flash_message=There was an error trying to access the admin portal';
    }
  } catch (error) {
    console.error('Error generating admin access token:', error);
    window.location.href = '/?flash_message=There was an error trying to access the admin portal';
  }
}

function buildAccountMenuItems(
  appCopy: (key: string) => string,
  hubAppCopy: (key: string) => unknown,
  currentUser: Record<string, unknown>,
  navigate: (path: string) => void
): AccountMenuItem[] {
  const items: AccountMenuItem[] = []

  // License admins only
  if (currentUser.customerTypeId == 2) {
    const portalCopy = hubAppCopy('portalCopy') as { manageOrganization?: string }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items.push({ name: portalCopy?.manageOrganization || 'Manage Organization', icon: Building as any, onSelect: goToManageOrganization })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items.push({ name: appCopy('editProfile'), icon: Settings as any, onSelect: () => navigate('/profile') })

  // Manage Subscription only if user has subscription access (Stripe subscription or
  // granted membership - NOT HSS-only users)
  const showSubscriptionManagement = Boolean(
    (currentUser as Record<string, unknown> & { showSubscriptionManagement?: boolean }).showSubscriptionManagement
  );
  if (showSubscriptionManagement) {
    const manageSubscriptionLabel =
      (hubAppCopy('portalCopy') as { subscriptionCopy?: { manage?: { headerTitle?: string } } } | undefined)
        ?.subscriptionCopy?.manage?.headerTitle || 'Manage Subscription';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items.push({ name: manageSubscriptionLabel, icon: CreditCard as any, onSelect: () => navigate('/subscription/manage') })
  }

  // Kaiya MCP is an account-level add-on; only eligible users see the connect page.
  // Mirrors the /profile/ai-tools route guard (ProductAccessRoute prosci_ai) so the
  // link never points at a route that silently redirects.
  if (
    currentUser.hasKaiyaMcpAccess &&
    (currentUser.products as Array<{ code?: string }> | undefined)?.some((p) => p.code === 'prosci_ai')
  ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items.push({ name: 'Connect AI Tools', icon: ExternalLink as any, onSelect: () => navigate('/profile/ai-tools') })
  }

  return items
}

// Layout component that wraps all view components.
function NavigationLayout({
  children,
  userApps,
  programs,
  program,
  activeHubSlug,
  product,
  masqueradedUser
}: NavigationLayoutProps) {
  const programName = program ? program.programName : null;
  const hubAppCopy = useHubAppCopy();
  
  // Pages that should not have Mission Control sidebar
  const pathsWithoutSidebar = [
    '/',
    '/profile',
    '/bookmarks',
    '/search',
    '/contact',
    '/kaiya',
    '/prosci_ai',
    '/subscription/manage',
    '/sign_on',
    '/logout'
  ]
  
  const isPathWithoutSidebar = pathsWithoutSidebar.includes(window.location.pathname) || 
    window.location.pathname.startsWith('/register/') ||
    window.location.pathname.startsWith('/resources/') ||
    window.location.pathname.startsWith('/kaiya/shared/')

  // Check if we're on Kaiya page (has its own sidebar that uses shared state)
  const isKaiyaPage = window.location.pathname === '/kaiya' || window.location.pathname === '/prosci_ai'

  // Determine if current page should have sidebar  
  const shouldShowSidebar = product !== 'change_coach' && activeHubSlug && !isPathWithoutSidebar
  const legacyMissionControlEnabled =
    isKaiyaPage || ((!!product || !!activeHubSlug) && !isPathWithoutSidebar)
  const [
    expandedNavItems,
    expandedNavItemProgramName,
    setExpandedNavItems,
    setExpandedNavItemProgramName,
  ] = useExpandedNavItems();
  const [expandedSideNav, setExpandedSideNav] = useExpandedSideNav();
  const appCopy = useAppCopy();
  const { user: currentUser } = useApp();
  const workspaces = useWorkspaces(currentUser);

  // set and maintain the side nav expanded items based on active program
  useEffect(() => {
    setExpandedNavItemProgramName(programName);
  }, [programName]);

  // URLs for terms and privacy policy links in dropdown
  const urls = {
    terms: TERMS_CONDITIONS_LINK || '',
    privacy: PRIVACY_POLICY_LINK || '',
  }

  return (
    <SidebarProvider defaultOpen={expandedSideNav} open={expandedSideNav} onOpenChange={setExpandedSideNav}>
      <NavigationLayoutContent
        appCopy={appCopy}
        hubAppCopy={hubAppCopy}
        currentUser={currentUser}
        masqueradedUser={masqueradedUser}
        userApps={userApps}
        programs={programs}
        program={program}
        programName={programName}
        activeHubSlug={activeHubSlug}
        product={product}
        expandedNavItems={expandedNavItems}
        expandedNavItemProgramName={expandedNavItemProgramName}
        expandedSideNav={expandedSideNav}
        setExpandedNavItems={setExpandedNavItems}
        setExpandedSideNav={setExpandedSideNav}
        legacyMissionControlEnabled={legacyMissionControlEnabled}
        shouldShowSidebar={shouldShowSidebar}
        isKaiyaPage={isKaiyaPage}
        urls={urls}
        workspaces={workspaces}
      >
        {children}
      </NavigationLayoutContent>
    </SidebarProvider>
  );
}

// Inner component that can use useSidebar hook
interface NavigationLayoutContentProps {
  children: ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appCopy: (key: string) => any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hubAppCopy: (key: string) => any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  masqueradedUser?: any
  userApps: string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  programs: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  program?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  programName: any
  activeHubSlug?: string
  product?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expandedNavItems: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expandedNavItemProgramName?: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expandedSideNav: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setExpandedNavItems: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setExpandedSideNav: any
  legacyMissionControlEnabled: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shouldShowSidebar: any
  isKaiyaPage: boolean
  urls: { terms: string; privacy: string }
  workspaces: WorkspaceOption[]
}

function NavigationLayoutContent({
  children,
  appCopy,
  hubAppCopy,
  currentUser,
  masqueradedUser,
  userApps,
  programs,
  program,
  programName,
  activeHubSlug,
  product,
  expandedNavItems,
  expandedNavItemProgramName,
  expandedSideNav,
  setExpandedNavItems,
  setExpandedSideNav,
  legacyMissionControlEnabled,
  shouldShowSidebar,
  isKaiyaPage,
  urls,
  workspaces,
}: NavigationLayoutContentProps) {
  const { toggleSidebar, state, isMobile, openMobile } = useSidebar()
  const { stopMasquerading, refreshUser } = useAuth()
  // Below the mobile breakpoint, toggleSidebar() flips `openMobile` (not `open`/`state`) —
  // this custom drawer has to read that instead of `state` to stay in sync on mobile.
  const isCollapsed = isMobile ? !openMobile : state === 'collapsed'
  const navigate = useViewTransitionNavigate()
  const canUseWorkspaces = hasWorkspacesAccess(currentUser)
  const showLegacyMissionControl =
    legacyMissionControlEnabled && !canUseWorkspaces

  // Mirrors the /kaiya route guard (ProductAccessRoute prosci_ai)
  const hasKaiyaAccess = (currentUser?.products as Array<{ code?: string }> | undefined)?.some(
    (p) => p.code === 'prosci_ai'
  )

  const missionControlWidth =
    showLegacyMissionControl && !isCollapsed && !isMobile ? 70 : 0
  const sideNavWidth = shouldShowSidebar && !isCollapsed ? 255 : 0
  const contentLeft = missionControlWidth + sideNavWidth
  const layoutStyle = {
    '--mission-control-width': `${missionControlWidth}px`,
    '--content-left': `${contentLeft}px`,
  } as React.CSSProperties

  // Top-left brand mirrors the hub you're browsing (Knowledge/Research/Instructor Hub),
  // falling back to Prosci Portal when there's no active hub context.
  const hubMapping = shouldShowSidebar && activeHubSlug ? APP_MAPPING_BY_SLUG[activeHubSlug] : undefined
  const brandIcon = hubMapping ? APP_IMAGE_MAPPING[hubMapping.code as keyof typeof APP_IMAGE_MAPPING] : APP_IMAGE_MAPPING['portal']
  const brandName = hubMapping ? appCopy(hubMapping.code) : 'Prosci Portal'
  const brandHref = hubMapping ? `/${activeHubSlug}` : '/'

  const portalCopy = hubAppCopy('portalCopy') as { customerTypes?: { direct?: string; licenseAdmin?: string; licenseUser?: string } } ?? {}
  const handleStopMasquerading = async () => {
    try {
      await stopMasquerading()
      // stopMasquerading handles the state update and redirect
    } catch (error) {
      console.error('Error stopping masquerading:', error)
      window.location.href = '/logout'
    }
  }

  // The dashboard owns the product-launch flow (terms gating, access URLs), so tiles
  // hand off to it through the `launchProduct` param rather than duplicating it here.
  const launchProduct = React.useCallback(
    (productId: string) => navigate(`/?launchProduct=${encodeURIComponent(productId)}`),
    [navigate]
  )
  const { data: drawerProducts = [], isLoading: isDrawerLoading } = useProductDrawerQuery(
    currentUser?.id,
    currentUser?.activeWorkspaceId,
    canUseWorkspaces
  )
  const productSwitcherApps = useProductSwitcherApps(
    drawerProducts as DrawerProduct[],
    launchProduct
  )

  const activeWorkspaceId = canUseWorkspaces && currentUser?.activeWorkspaceId != null
    ? String(currentUser.activeWorkspaceId)
    : undefined
  // Branding follows the active workspace once workspaces are in play, so the
  // account logo is only a fallback for users without the feature.
  const accountLogo = currentUser?.account?.logo
    ? { url: currentUser.account.logo, alt: currentUser.account.name || 'Company logo' }
    : undefined
  const handleWorkspaceSelect = async (workspace: AccountMenuWorkspace) => {
    if (!workspace?.id || workspace.id === activeWorkspaceId) return
    try {
      const { api } = await import('../../utils/apiClient')
      await api.activateWorkspace(workspace.id)
      // The active workspace is server-side state; re-fetching the user is
      // what re-renders the dashboard tools for the new context.
      await refreshUser()
    } catch (error) {
      console.error('Failed to switch workspace:', error)
    }
  }

  return (
    <>
      {showLegacyMissionControl && (
        <div className="fixed left-0 top-0 z-50 hidden h-full md:block">
          <MissionControl
            className="h-full"
            appCopy={appCopy}
            currentNavItem={
              isKaiyaPage
                ? APP_PATH_MAPPING.changeCoach
                : activeHubSlug
                  ? APP_MAPPING_BY_SLUG[activeHubSlug]?.path
                  : undefined
            }
            expanded={!isCollapsed}
            homeNavItem={{
              path: APP_PATH_MAPPING.portal,
              logo: APP_IMAGE_MAPPING.portal,
              logoAlt: 'Prosci Apps',
            }}
            navItems={missionControlNavItems(userApps, appCopy, hubAppCopy)}
            onNavigate={navigate}
          />
        </div>
      )}

      {/* SideNav - collapsible content sidebar */}
      {shouldShowSidebar && (
        <>
          {/* Backdrop for small viewports: tap outside to close the sidebar drawer */}
          {!isCollapsed && (
            <div
              className="md:hidden fixed inset-0 bg-black/50 z-30"
              onClick={toggleSidebar}
              aria-hidden="true"
            />
          )}
          <div
            className="fixed left-0 top-14 z-40 h-[calc(100%-3.5rem)] w-[255px] transition-transform duration-200 ease-linear md:left-[var(--mission-control-width)]"
            style={{
              ...layoutStyle,
              transform: isCollapsed ? 'translateX(-100%)' : 'translateX(0)',
            } as React.CSSProperties}
          >
            <SideNav
              activeTopLevelNav={programName}
              currentPath={window.location.pathname}
              expandedNavItems={expandedNavItems}
              expandedNavItemProgramName={expandedNavItemProgramName}
              expandedSideNav={expandedSideNav}
              programs={programs}
              setExpandedNavItems={setExpandedNavItems}
              setExpandedSideNav={setExpandedSideNav}
              program={program}
              activeHubSlug={activeHubSlug}
              missionControlOffset={0}
            />
          </div>
        </>
      )}

      {/* Top Navigation - full-width bar, sidebar (if any) sits below it */}
      <div
        className="fixed right-0 top-0 z-50 left-0 md:left-[var(--mission-control-width)]"
        style={layoutStyle}
      >
        <Navigation
          className={isKaiyaPage ? 'bg-transparent' : undefined}
          brand={
            isKaiyaPage ? (
              <a href="/kaiya" className="flex items-center transition-opacity duration-200 hover:opacity-90">
                <img src={kaiyaIcon} alt="Kaiya" className="h-8 w-8 sm:hidden" />
                <img src={kaiyaLogo} alt="Kaiya" className="hidden h-9 w-auto sm:block" />
              </a>
            ) : (
              <NavBrand icon={<img src={brandIcon} alt={brandName} className="h-6 w-6" />} name={brandName} href={brandHref} />
            )
          }
          onToggleSidebar={shouldShowSidebar || isKaiyaPage ? toggleSidebar : undefined}
          toggleSidebarLabel={appCopy('toggleSideNavigation') || 'Toggle sidebar'}
          sidebarCollapsed={isCollapsed}
        >
          <NavActions items={topNavActionItems(hubAppCopy, navigate)} />
          {/* New Chat button for Kaiya pages - mobile only (desktop has the Kaiya sidebar) */}
          {isKaiyaPage && (
            <NavIconButton
              label="Start new chat"
              className="md:hidden"
              onClick={() => navigate(`/kaiya?new=${Date.now()}`)}
              icon={<PencilLine className="w-5 h-5" />}
            />
          )}
          {hasKaiyaAccess && (
            <NavIconButton
              label="Kaiya"
              onClick={() => navigate('/kaiya')}
              icon={<img src={kaiyaIcon} alt="" className="h-5 w-5" />}
            />
          )}
          {canUseWorkspaces && (
            <AppSwitcherButton
              apps={productSwitcherApps}
              isLoading={isDrawerLoading}
              portalHomeHref={window.location.pathname === '/' ? undefined : '/'}
            />
          )}
          <AccountMenu
            user={{
              name: currentUser?.firstName,
              initials: getInitials(currentUser?.firstName || ''),
              role: getPortalRoleLabel(currentUser, portalCopy),
            }}
            accountLogo={accountLogo}
            workspaces={canUseWorkspaces ? toAccountMenuWorkspaces(workspaces) : undefined}
            activeWorkspaceId={activeWorkspaceId}
            onWorkspaceSelect={canUseWorkspaces ? handleWorkspaceSelect : undefined}
            menuItems={buildAccountMenuItems(appCopy, hubAppCopy, currentUser, navigate)}
            secondaryItems={[
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              { name: appCopy('help'), icon: HelpCircle as any, external: true, href: product == 'change_coach' ? AI_HELP_LINK : CONTACT_LINK },
              ...(masqueradedUser ? [{ name: 'Stop Masquerading', onSelect: handleStopMasquerading }] : []),
            ]}
            onLogout={masqueradedUser ? undefined : () => navigate('/logout')}
            logoutLabel={appCopy('logout')}
            termsHref={urls.terms}
            privacyHref={urls.privacy}
          />
        </Navigation>
      </div>

      {/* Main Content - takes remaining space, pushed down below the fixed top bar */}
      <SidebarInset
        className="relative z-0 bg-muted min-h-screen pt-14 transition-[margin] duration-200 ease-linear md:!ml-[var(--content-left)]"
        style={layoutStyle}
        id="main-content"
      >
        {children}
      </SidebarInset>
    </>
  );
}


export default NavigationLayout
